import React, { useState, useEffect, useRef } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Input } from "../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../design/fieldset";
import { useFiling } from "../contexts/FilingContext";
import { SideType, sideTypes } from "../models/SideType";
import { Select } from "../design/select";
import { PlusIcon } from "@heroicons/react/20/solid";

interface Props {
  className?: string;
  children?: React.ReactNode;
}

interface Errors {
  name?: string;
  file?: string;
  side?: string;
}

const UploadFilingButton: React.FC<Props> = ({ className, children }) => {
  const { createFiling } = useFiling();
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState<string>("");
  const [filingDate, setFilingDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [side, setSide] = useState<SideType>("me");
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!name.trim()) newErrors.name = "A name is required.";
    if (!file) newErrors.file = "A file is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (file) {
      setName(file.name);
    }
  }, [file]);

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setErrors({});
    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;
    if (
      ![
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ].includes(droppedFile.type)
    )
      return setErrors({
        file: "Invalid file type. Only PDF and Word files are allowed.",
      });
    if (droppedFile.size > 10 * 1024 * 1024)
      // 10 MB limit
      return setErrors({ file: "File size exceeds 10 MB." });
    setFile(droppedFile);
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsUploading(true);

    try {
      await createFiling(file!, {
        name,
        filingDate: filingDate ? new Date(filingDate) : undefined,
        side,
      });
      clearForm();
    } catch (error) {
      setErrors({ name: "Failed to create filing. Please try again." });
    }

    setIsUploading(false);
  };

  const clearForm = () => {
    if (file) {
      // If a file is present, return to Step 1 instead of closing
      setFile(null);
      setErrors({});
    } else {
      // No file? Close the dialog
      setFile(null);
      setName("");
      setFilingDate(new Date().toISOString().split("T")[0]);
      setSide("me");
      setErrors({});
      setIsOpen(false);
    }
  };

  return (
    <>
      <Button
        className={className}
        type="button"
        onClick={() => setIsOpen(true)}
      >
        {children ?? (
          <div className="flex items-center gap-2">
            <PlusIcon className="w-4 h-4" />
            <span className="hidden sm:block">Upload Filing</span>
          </div>
        )}
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Upload Filing</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              {!file ? (
                <div
                  className="flex flex-col items-center justify-center p-6 text-sm text-center border-2 border-dashed rounded-md cursor-pointer text-zinc-500 hover:bg-zinc-50 dark:hover:bg-zinc-800"
                  onDrop={handleFileDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={handleFileClick}
                >
                  <p>Drag and drop a file here, or click to browse</p>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    ref={fileInputRef}
                    onChange={(e) =>
                      setFile(e.target.files ? e.target.files[0] : null)
                    }
                  />
                  {errors.file && <ErrorMessage>{errors.file}</ErrorMessage>}
                </div>
              ) : (
                <>
                  <Field>
                    <Label>Name</Label>
                    <Input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Name"
                    />
                    {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                  </Field>
                  <Field>
                    <Label>Filing Date</Label>
                    <Input
                      type="date"
                      value={filingDate}
                      onChange={(e) => setFilingDate(e.target.value)}
                    />
                  </Field>
                  <Field>
                    <Label>Side</Label>
                    <Select
                      value={side}
                      onChange={(e) => setSide(e.target.value as SideType)}
                    >
                      <option value="" disabled>
                        Select side
                      </option>
                      {Object.entries(sideTypes).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ))}
                    </Select>
                    {errors.side && <ErrorMessage>{errors.side}</ErrorMessage>}
                  </Field>
                </>
              )}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            {file ? "Back" : "Close"}
          </Button>
          <Button
            disabled={!file || !name.trim() || isUploading}
            onClick={handleSubmit}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadFilingButton;
